import './kp-search-input.less';
import type {IAttributes, IAugmentedJQuery, IDeferred, ILogService, INgModelController, IPromise, IQService, IScope} from 'angular';
import type {SearchInputService} from './search-input.provider';
import type {Component, OnChangesObject} from 'typings/portaro.fe.types';
import type {OnChanges} from 'typings/portaro.fe.types';

abstract class SearchInputComponentController implements SearchInputComponentBindings, OnChanges<SearchInputComponentBindings> {
    public model: string;
    public autofocus: 'true' | 'false';
    public cssClass: string;
    public cssId: string;
    public placeholder: string;
    public prefilledValue: string;

    private deferredValues: IDeferred<string[]>;
    private ngModelController: INgModelController;
    private $inputElement: IAugmentedJQuery;
    private loading: boolean;
    private loadingSpinnerTop: string;

    /*@ngInject*/
    constructor(private searchInputService: SearchInputService, private $element: IAugmentedJQuery, private $attrs: IAttributes, private $log: ILogService, private $scope: IScope, private $q: IQService) {
        this.deferredValues = $q.defer();
    }

    public abstract onSubmit(passedValues: { searchPhrase: string });

    public $onInit() {
        this.searchInputService.getAutocompleteValuesStream().subscribe(
            (autocompleteValues) => {
                this.deferredValues.resolve(autocompleteValues);
                this.loading = false;
                this.deferredValues = this.$q.defer();
            }
        );

        if (this.ngModelController) {
            this.ngModelController.$render = () => {
                this.model = this.ngModelController.$modelValue || this.prefilledValue;
            };
        }

        this.searchInputService.getCurrentSearchPhraseStream().subscribe((value) => {
            if (this.model !== value) {
                this.model = value;
            }
        });

        if (this.prefilledValue) {
            this.searchInputService.setSearchValue(this.prefilledValue);
            this.model = this.prefilledValue;
        }
    }

    public $postLink() {
        setTimeout(() => {
            if (this.autofocus === 'true') {
                this.$inputElement[0].focus();
            }
        });

        this.$inputElement.addClass(this.cssClass || '');
        this.$inputElement.attr('id', this.cssId || '');

        // (heightOfInput / 2 - heightOfSpinner / 2)
        this.loadingSpinnerTop = `${this.$inputElement[0].getBoundingClientRect().height / 2 - 8}px`;
    }

    public $onChanges(onChangesObj: OnChangesObject<SearchInputComponentBindings>): void {
        if (!onChangesObj.autofocus.isFirstChange() && onChangesObj.autofocus.currentValue === 'true') {
            this.$inputElement[0].focus();
        }
    }

    public onPressEnter(searchPhrase) {
        if (this.onSubmit) {
            this.onSubmit({searchPhrase});
        }
    }

    public onSelect($item) {
        this.searchInputService.triggerSearch($item);
    }

    public updateSearchPhrase(searchPhrase: string) {
        this.loading = searchPhrase !== '';
        this.searchInputService.updateSearchValue(searchPhrase);
        if (this.ngModelController) {
            this.ngModelController.$setViewValue(searchPhrase);
        }
    }

    public getAutocompleteValues(): IPromise<string[]> {
        return this.deferredValues.promise;
    }
}

/**
 * @ngdoc component
 * @name kpSearchInput
 * @module portaro.components.search-input
 *
 * @param {string=} ngModel NgModel
 *
 * @param {string=} prefilledValue Prefilled value
 * @param {expression=} onSubmit If user pressed `enter` key, expression is evaluated with `searchValue` variable
 * @param {boolean=} autofocus Set autofocus
 *
 * @param {string=} cssClass Css classes passed to input element
 * @param {string=} cssId ID passed to input element
 * @param {string=} placeholder Placeholder
 *
 * @description
 * Component provide synchronization of content with other kpSearchInputs.
 * Render classic text input with async autocomplete. Autocomplete search function is configured via {@link portaro.components.search-input.searchInputServiceProvider SearchInputServiceProvider}
 *
 * Value can be accessed from {@link portaro.components.search-input.searchInputService#getSearchValue SearchInputService.getSearchValue()}
 */
// eslint-disable-next-line
export default class SearchInputComponent implements Component<SearchInputComponentBindings> {
    public static componentName = 'kpSearchInput';

    public templateUrl = require('./kp-search-input.tpl.pug');

    public require = {
        ngModelController: '?ngModel'
    };

    public bindings = {
        onSubmit: '&?',
        autofocus: '@?',
        cssClass: '@?',
        cssId: '@?',
        placeholder: '@?',
        prefilledValue: '@?'
    } as const;

    public controller = SearchInputComponentController;
}

export interface SearchInputComponentBindings {
    autofocus: 'false' | 'true'; // string asi aby slo pouzit i v custom souborech bez parsovani
    cssClass: string;
    cssId: string;
    placeholder: string;
    prefilledValue: string;

    onSubmit(locals: { searchPhrase: string });
}
